import {
  companyUsesCcpV2,
  loadPermissionsInfo,
  redirectIfInactiveGroup,
  redirectToHomeIfNoCcpAccess,
  redirectToHomeIfNotAdmin,
  redirectToHomeIfNotPermission,
  redirectToNotFoundIfNoWaveId,
} from '@/ccpv2/router/guards';
import { IS_CCPV2_ROUTE } from '@/types';

const LayoutCompCycle = () => import('@/ccpv2/layouts/layout-comp-cycle.vue');
const CcpListPage = () => import('@/ccpv2/pages/ccp-list.vue');
const CcpDashboardPage = () => import('@/ccpv2/pages/ccp-dashboard.vue');
const CcpDetailsPage = () => import('@/ccpv2/pages/ccp-details.vue');
const CcpSettingsPage = () => import('@/ccpv2/pages/ccp-settings.vue');
const CcpSettingsGeneralPage = () => import('@/ccpv2/pages/ccp-settings-general.vue');
const CcpSettingsImportPage = () => import('@/ccpv2/pages/ccp-settings-import.vue');
const CcpSettingsExportPage = () => import('@/ccpv2/pages/ccp-settings-export.vue');
const CcpSettingsRewardLetterTemplatesPage = () => import('@/ccpv2/pages/ccp-settings-reward-letter-templates.vue');
const CcpSettingsSentRewardLettersPage = () => import('@/ccpv2/pages/ccp-settings-sent-reward-letters.vue');
const CcpSettingsPlanningHierarchyPage = () => import('@/ccpv2/pages/ccp-settings-planning-hierarchy.vue');
const CcpSettingsPlanningHierarchyWavePage = () => import('@/ccpv2/pages/ccp-settings-planning-hierarchy-wave.vue');
const CcpNotFoundPage = () => import('@/ccpv2/pages/ccp-not-found.vue');
const CcpDownloadFilePage = () => import('@/ccpv2/pages/ccp-download-file.vue');
const CcpColumnsConfigPage = () => import('@/ccpv2/pages/ccp-columns-config.vue');
const CcpColumnsConfigComputationDrawer = () =>
  import('@/ccpv2/components/ccp-columns-config/computation-drawer/computation-drawer.vue');
const CcpSettingsRolesPage = () => import('@/ccpv2/pages/ccp-settings-roles.vue');
const CcpSettingsNotificationsPage = () => import('@/ccpv2/pages/ccp-settings-notifications.vue');
const CcpSettingsHrisDashboardPage = () => import('@/ccpv2/pages/ccp-settings-hris-dashboard.vue');

const meta = {
  [IS_CCPV2_ROUTE]: true,
  layout: 'layout-default',
};

export const ccpRoutes = [
  {
    path: '',
    component: LayoutCompCycle,
    meta,
    children: [
      {
        path: '/ccpv2-list',
        name: 'ccpv2-list',
        component: CcpListPage,
        beforeEnter: [companyUsesCcpV2, redirectToHomeIfNoCcpAccess],
      },
      {
        path: '/ccpv2-dashboard/:cycleId',
        name: 'ccpv2-dashboard',
        component: CcpDashboardPage,
        beforeEnter: [redirectToHomeIfNotAdmin, companyUsesCcpV2],
      },
      {
        path: '/ccpv2-details/:cycleId/admin',
        name: 'ccpv2-admin-details',
        component: CcpDetailsPage,
        beforeEnter: [redirectToHomeIfNotAdmin, companyUsesCcpV2, loadPermissionsInfo],
      },
      {
        path: '/ccpv2-details/:cycleId/group/:groupId/role/:roleId',
        name: 'ccpv2-group-details',
        component: CcpDetailsPage,
        beforeEnter: [companyUsesCcpV2, loadPermissionsInfo, redirectIfInactiveGroup],
      },
      {
        path: '/ccpv2-columns-config/:cycleId',
        name: 'ccpv2-columns-config',
        component: CcpColumnsConfigPage,
        beforeEnter: [companyUsesCcpV2, redirectToHomeIfNotAdmin],
        children: [
          {
            path: 'computation/:columnName/:status?',
            name: 'ccpv2-columns-config-computation',
            component: CcpColumnsConfigComputationDrawer,
            props: true,
          },
        ],
      },
      // Settings pages for Admins
      {
        path: '/ccpv2-settings/:cycleId',
        redirect: { name: 'ccpv2-settings-general' },
        name: 'ccpv2-settings',
        component: CcpSettingsPage,
        beforeEnter: [companyUsesCcpV2, redirectToHomeIfNotAdmin],
        children: [
          {
            path: 'general',
            name: 'ccpv2-settings-general',
            component: CcpSettingsGeneralPage,
          },
          {
            path: 'import',
            name: 'ccpv2-settings-import',
            component: CcpSettingsImportPage,
          },
          {
            path: 'export',
            name: 'ccpv2-settings-export',
            component: CcpSettingsExportPage,
          },
          {
            path: 'reward-letter-templates',
            name: 'ccpv2-settings-reward-letter-templates',
            component: CcpSettingsRewardLetterTemplatesPage,
          },
          {
            path: 'sent-reward-letters',
            name: 'ccpv2-settings-sent-reward-letters',
            component: CcpSettingsSentRewardLettersPage,
          },
          {
            path: 'planning-hierarchy',
            name: 'ccpv2-settings-planning-hierarchy',
            component: CcpSettingsPlanningHierarchyPage,
            beforeEnter: [redirectToNotFoundIfNoWaveId],
            children: [
              {
                path: ':waveId',
                name: 'ccpv2-settings-planning-hierarchy-wave',
                component: CcpSettingsPlanningHierarchyWavePage,
              },
            ],
          },
          {
            path: 'roles',
            name: 'ccpv2-settings-roles',
            component: CcpSettingsRolesPage,
          },
          {
            path: 'notifications',
            name: 'ccpv2-settings-notifications',
            component: CcpSettingsNotificationsPage,
          },
          {
            path: 'hris-dashboard',
            name: 'ccpv2-settings-hris-dashboard',
            component: CcpSettingsHrisDashboardPage,
          },
        ],
      },
      // Settings pages for Non-Admins
      {
        path: '/ccpv2-group-settings/:cycleId/group/:groupId/role/:roleId',
        redirect: { name: 'ccpv2-group-settings-export' },
        name: 'ccpv2-group-settings',
        component: CcpSettingsPage,
        beforeEnter: [companyUsesCcpV2, redirectToHomeIfNotPermission('can_export_data')],
        children: [
          {
            path: 'export',
            name: 'ccpv2-group-settings-export',
            component: CcpSettingsExportPage,
          },
        ],
      },
    ],
  },
  // Routes that don't need CCP header
  {
    path: '/ccpv2-download-file',
    name: 'ccpv2-download-file',
    component: CcpDownloadFilePage,
    beforeEnter: [companyUsesCcpV2],
    meta,
  },
  {
    path: '/ccpv2-not-found',
    name: 'ccpv2-only-404',
    component: CcpNotFoundPage,
    meta,
  },
];
